import { useEffect, useState, useRef } from 'react';
import debounce from 'lodash/debounce';

/**
 * A hook that debounces a value using Lodash's debounce
 * @param value The value to debounce
 * @param delay The delay in milliseconds (default: 300)
 * @returns The debounced value
 */
const useDebounce = <T extends unknown>(value: T, delay: number = 300): T => {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    const debouncedFn = useRef<ReturnType<typeof debounce>>();

    useEffect(() => {
        // Create the debounced function only once
        if (!debouncedFn.current) {
            debouncedFn.current = debounce((newValue: T) => {
                setDebouncedValue(newValue);
            }, delay);
        }

        // Call the debounced function whenever value changes
        debouncedFn.current(value);

        // Clean up the debounce on unmount
        return () => {
            debouncedFn.current?.cancel();
        };
    }, [value, delay]);

    return debouncedValue;
};

export default useDebounce;